import { Check, ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

import type { FilterOption } from './define'

export interface MultiSelectFilterProps<T extends string> {
    label: string
    options: readonly FilterOption<T>[]
    value: T[]
    onChange: (next: T[]) => void
    /** Adds a type-to-filter box. Worth it past roughly a dozen options. */
    searchable?: boolean
    /** For a filter another control has made irrelevant, rather than hiding it. */
    disabled?: boolean
    className?: string
}

export function MultiSelectFilter<T extends string>({
    label,
    options,
    value,
    onChange,
    searchable = false,
    disabled = false,
    className
}: MultiSelectFilterProps<T>) {
    const [open, setOpen] = useState(false)
    const selected = new Set(value)

    const toggle = (option: T) => {
        const next = new Set(selected)
        if (next.has(option)) next.delete(option)
        else next.add(option)
        // Emit in declaration order rather than click order, so the URL for a given
        // selection is always the same string and the query cache actually hits.
        onChange(options.filter(entry => next.has(entry.value)).map(entry => entry.value))
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={disabled}
                    aria-label={`${label} filter`}
                    // Cluster, not an isolated control: grown on a coarse pointer to
                    // match the other filter triggers instead of hit-slopped.
                    className={cn('justify-between gap-1.5 pointer-coarse:h-10', className)}>
                    <span className={value.length > 0 ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
                    {value.length > 0 && (
                        <>
                            <Separator orientation="vertical" className="h-3.5" />
                            {/* Naming the single selection beats a "1" badge: it saves
                                opening the menu to remember what is applied. */}
                            <span className="tabular max-w-32 truncate text-foreground">
                                {value.length === 1
                                    ? (options.find(option => option.value === value[0])?.label ?? value[0])
                                    : `${value.length} selected`}
                            </span>
                        </>
                    )}
                    <ChevronDown aria-hidden className="size-3.5 text-subtle-foreground" />
                </Button>
            </PopoverTrigger>

            <PopoverContent align="start" className="w-56 p-0">
                <Command>
                    {searchable && <CommandInput placeholder={`Filter ${label.toLowerCase()}`} />}
                    <CommandList>
                        <CommandEmpty>No matches</CommandEmpty>
                        <CommandGroup>
                            {options.map(option => {
                                const isSelected = selected.has(option.value)
                                return (
                                    <CommandItem
                                        key={option.value}
                                        value={option.label}
                                        onSelect={() => toggle(option.value)}
                                        role="option"
                                        aria-selected={isSelected}>
                                        <div
                                            aria-hidden
                                            className={cn(
                                                'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                                                isSelected
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-input'
                                            )}>
                                            {isSelected && <Check className="size-3" />}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="truncate">{option.label}</div>
                                            {option.hint && (
                                                <div className="truncate text-xs text-muted-foreground">
                                                    {option.hint}
                                                </div>
                                            )}
                                        </div>
                                    </CommandItem>
                                )
                            })}
                        </CommandGroup>
                    </CommandList>

                    {value.length > 0 && (
                        <div className="border-t border-border p-1">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => onChange([])}
                                className="w-full justify-center pointer-coarse:h-10">
                                Clear
                            </Button>
                        </div>
                    )}
                </Command>
            </PopoverContent>
        </Popover>
    )
}
